import React, { useEffect, useState } from "react";
import {
  FaCoffee,
  FaLock,
  FaExclamationTriangle,
  FaQrcode,
  FaDownload,
} from "react-icons/fa";
import NumberInput from "../../atoms/NumberInput";
import { ListSkeleton, EmptyState } from "../../shared";
import useAuth from "../../../Hooks/useAuth";
import { useAlert } from "../../../context/AlertContext";
import {
  useReadEspressoBeveragesQuery,
  useReadEspressoPaymentQrQuery,
  useUpdateEspressoBeveragePriceMutation,
} from "../../../store/storageRoles/devicesSettingsV2Api";
import { formatTl } from "./settingsTabV2/paymentFormat";

// Espresso (Kalerm) içecek listesi. Server her açılışta Kalerm 2.4 ile
// senkronlar (upsert + stale silme); burada fiyat (kuruş) düzenlenir.
export default function EspressoBeveragesTab({ selectedDevice }) {
  const { auth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const id = selectedDevice?._id;
  const token = auth?.accessToken;

  const { data, isLoading } = useReadEspressoBeveragesQuery(
    { id, token },
    { skip: !id || !token, refetchOnMountOrArgChange: true }
  );
  const [updatePrice] = useUpdateEspressoBeveragePriceMutation();

  // QR ödeme kodu — server env QR key ile şifreleyip PNG data URL döner.
  const { data: qrData, isFetching: qrLoading } = useReadEspressoPaymentQrQuery(
    { id, token },
    { skip: !id || !token }
  );
  const qrDataUrl = qrData?.qrDataUrl;

  const handleDownloadQr = () => {
    if (!qrDataUrl) return;
    const a = document.createElement("a");
    a.href = qrDataUrl;
    a.download = `qr-odeme-${selectedDevice?.serialNo || "espresso"}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const beverages = data?.beverages || [];
  const synced = data?.synced;

  // Düzenlenen fiyatlar (kuruş), drinkId bazlı local state.
  const [prices, setPrices] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    const map = {};
    (data?.beverages || []).forEach((b) => {
      map[b.drinkId] = b.price ?? 0;
    });
    setPrices(map);
  }, [data]);

  const handleSave = async (b) => {
    setSavingId(b.drinkId);
    try {
      await updatePrice({
        token,
        id,
        drinkId: b.drinkId,
        price: Math.max(0, Math.trunc(Number(prices[b.drinkId]) || 0)),
      }).unwrap();
      showSuccess("Fiyat güncellendi.");
    } catch (err) {
      showError(err?.data?.message || "Fiyat güncellenemedi, tekrar deneyin.");
    } finally {
      setSavingId(null);
    }
  };

  const qrCard = (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-xl bg-main/5">
          <FaQrcode className="w-5 h-5 text-main" />
        </div>
        <div className="min-w-0">
          <h4 className="text-base font-semibold text-gray-800">
            QR Ödeme Kodu
          </h4>
          <p className="text-xs text-gray-400">
            Bu cihaza özel ödeme QR'ı — yazdırıp makineye yapıştırabilirsiniz.
          </p>
        </div>
      </div>
      <div className="flex flex-col items-center gap-3">
        {qrLoading && !qrDataUrl ? (
          <div className="w-44 h-44 rounded-xl bg-gray-100 animate-pulse" />
        ) : qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt="QR Ödeme Kodu"
            className="w-44 h-44 rounded-xl border border-gray-200"
          />
        ) : (
          <div className="w-44 h-44 rounded-xl bg-newtwo border border-fifth/30 flex items-center justify-center text-xs text-fifth text-center px-3">
            QR üretilemedi
          </div>
        )}
        <button
          type="button"
          disabled={!qrDataUrl}
          onClick={handleDownloadQr}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-main text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FaDownload className="w-4 h-4" />
          <span>QR'ı İndir</span>
        </button>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="p-3 sm:p-4 space-y-4">
        {qrCard}
        <div className="relative">
          <ListSkeleton
            columns={[{ label: "İçecek" }, { label: "Fiyat" }]}
            rowCount={6}
            mobileCardCount={4}
            message="İçecekler yükleniyor..."
          />
        </div>
      </div>
    );
  }

  if (!beverages.length) {
    return (
      <div className="p-3 sm:p-4 space-y-4">
        {qrCard}
        <EmptyState
          message="İçecek bulunamadı"
          suggestion={
            synced === false
              ? "Cihaza bulut platformundan ulaşılamadı; liste alınamadı."
              : "Bu cihaza tanımlı içecek bulunmuyor."
          }
        />
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 bg-gray-50/40 space-y-4">
      {qrCard}
      {synced === false && (
        <div className="flex items-start gap-2 mb-4 p-3 rounded-xl bg-newtwo border border-fifth/30 text-sm text-fifth">
          <FaExclamationTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <p>
            Bulut platformuna ulaşılamadı — liste en güncel halini
            yansıtmıyor olabilir. Fiyatlar kayıtlı değerlerden gösteriliyor.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {beverages.map((b) => {
          const kurus = prices[b.drinkId] ?? 0;
          const dirty = Number(kurus) !== Number(b.price ?? 0);
          const saving = savingId === b.drinkId;
          return (
            <div
              key={b.drinkId}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-main/5">
                  <FaCoffee className="w-5 h-5 text-main" />
                </div>
                <div className="min-w-0">
                  <h4 className="text-base font-semibold text-gray-800 truncate">
                    {b.drinkName || "İsimsiz içecek"}
                  </h4>
                  <p className="text-xs text-gray-400">
                    drinkId: {b.drinkId}
                  </p>
                </div>
                {/* Kilit yalnızca bilgi/görüntü amaçlı — hiçbir işlemi engellemez. */}
                {b.locked && (
                  <span className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-fifth bg-newtwo px-2 py-1 rounded-lg shrink-0">
                    <FaLock className="w-3 h-3" /> Kilitli
                  </span>
                )}
              </div>

              <NumberInput
                label="Fiyat"
                value={kurus}
                min={0}
                suffix="kuruş"
                onChange={(v) =>
                  setPrices((prev) => ({
                    ...prev,
                    [b.drinkId]:
                      v === "" ? 0 : Math.max(0, Math.trunc(Number(v) || 0)),
                  }))
                }
              />
              <p className="text-xs text-gray-500 mt-1 font-medium">
                = {formatTl(kurus)}
              </p>

              <button
                type="button"
                disabled={!dirty || saving}
                onClick={() => handleSave(b)}
                className="mt-3 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-main text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Kaydediliyor...</span>
                  </>
                ) : (
                  <span>{dirty ? "Kaydet" : "Kayıtlı"}</span>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
